import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Pin, AlertCircle } from 'lucide-react';
import {
  announcementsApi,
  type Announcement,
  type AnnouncementCategory,
  type AnnouncementTargetApp,
  type GetAnnouncementsParams,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  TextInput,
  Select,
  Pagination,
} from '@/components/common';
import {
  DataGrid,
  DateCell,
  makeBadgeCell,
  makeActionCell,
  RowNumberCell,
} from '@/components/grid';

type TargetFilter = AnnouncementTargetApp | 'ALL';
type CategoryFilter = AnnouncementCategory | 'ALL';

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

const CATEGORY_LABEL: Record<AnnouncementCategory, string> = {
  SYSTEM: '시스템',
  MAINTENANCE: '점검',
  EVENT: '이벤트',
  GENERAL: '일반',
};

const CATEGORY_TONE: Record<AnnouncementCategory, 'info' | 'warning' | 'success' | 'neutral'> = {
  SYSTEM: 'info',
  MAINTENANCE: 'warning',
  EVENT: 'success',
  GENERAL: 'neutral',
};

export function AnnouncementListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(10);
  const [appliedSearch, setAppliedSearch] = useState<string | undefined>();
  const [appliedTarget, setAppliedTarget] = useState<TargetFilter>('ALL');
  const [appliedCategory, setAppliedCategory] = useState<CategoryFilter>('ALL');

  const [localSearch, setLocalSearch] = useState('');
  const [localTarget, setLocalTarget] = useState<TargetFilter>('ALL');
  const [localCategory, setLocalCategory] = useState<CategoryFilter>('ALL');
  const [localSize, setLocalSize] = useState(10);

  const queryParams = useMemo<GetAnnouncementsParams>(
    () => ({
      page: pageIndex,
      size,
      targetApp: appliedTarget,
      category: appliedCategory,
      search: appliedSearch,
    }),
    [pageIndex, size, appliedTarget, appliedCategory, appliedSearch],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['announcements', queryParams],
    queryFn: () => announcementsApi.getAnnouncements(queryParams),
    placeholderData: prev => prev,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  const togglePin = useMutation({
    mutationFn: (row: Announcement) =>
      announcementsApi.updateAnnouncementFlags(row.id, {
        isPinned: !row.isPinned,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  const handleSearch = () => {
    setPageIndex(0);
    setSize(localSize);
    setAppliedSearch(localSearch.trim() || undefined);
    setAppliedTarget(localTarget);
    setAppliedCategory(localCategory);
  };

  const handleReset = () => {
    setLocalSearch('');
    setLocalTarget('ALL');
    setLocalCategory('ALL');
    setLocalSize(10);
    setPageIndex(0);
    setSize(10);
    setAppliedSearch(undefined);
    setAppliedTarget('ALL');
    setAppliedCategory('ALL');
  };

  const columns = useMemo<ColDef<Announcement>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '대상앱',
        field: 'targetApp',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: { BUSINESS: 'info', GUARDIAN: 'success' },
          labelMap: { BUSINESS: '비즈니스앱', GUARDIAN: '보호자앱' },
        }),
      },
      {
        headerName: '카테고리',
        field: 'category',
        width: 110,
        cellRenderer: makeBadgeCell({
          toneMap: CATEGORY_TONE,
          labelMap: CATEGORY_LABEL,
        }),
      },
      {
        headerName: '제목',
        field: 'title',
        flex: 1.5,
        minWidth: 240,
        cellRenderer: (p: ICellRendererParams<Announcement>) => {
          const row = p.data;
          if (!row) return '-';
          return (
            <div className="flex h-full items-center gap-1.5">
              {row.isPinned && (
                <span
                  title="상단 고정"
                  className="inline-flex items-center gap-0.5 rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700"
                >
                  <Pin size={11} />
                  고정
                </span>
              )}
              {row.isImportant && (
                <span
                  title="중요 공지"
                  className="inline-flex items-center gap-0.5 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"
                >
                  <AlertCircle size={11} />
                  중요
                </span>
              )}
              <span
                className={`truncate ${row.isImportant ? 'font-semibold text-gray-900' : ''}`}
                title={row.title}
              >
                {row.title}
              </span>
            </div>
          );
        },
      },
      {
        headerName: '게시 기간',
        width: 240,
        sortable: false,
        valueGetter: p => {
          const row = p.data;
          if (!row) return '';
          if (!row.displayStartAt && !row.displayEndAt) return '제한 없음';
          const fmt = (dt?: string | null) =>
            dt ? dt.slice(0, 16).replace('T', ' ') : '-';
          return `${fmt(row.displayStartAt)} ~ ${fmt(row.displayEndAt)}`;
        },
      },
      {
        headerName: '등록일',
        field: 'createdAt',
        width: 160,
        cellRenderer: DateCell,
      },
      {
        headerName: '관리',
        width: 160,
        sortable: false,
        cellRenderer: makeActionCell<Announcement>([
          {
            label: '상세',
            onClick: row => navigate(`/announcements/${row.id}`),
          },
          {
            label: '고정',
            onClick: row => togglePin.mutate(row),
            show: row => !row.isPinned,
          },
          {
            label: '해제',
            onClick: row => togglePin.mutate(row),
            show: row => row.isPinned,
            variant: 'danger',
          },
        ]),
      },
    ],
    [navigate, startIndex],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="공지사항"
        subtitle="멍투데이 시스템 업데이트 공지를 관리합니다. (비즈니스앱 / 보호자앱 구분 등록)"
        right={
          <button
            type="button"
            onClick={() => navigate('/announcements/new')}
            className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            공지사항 등록
          </button>
        }
      />

      <FilterBar onSubmit={handleSearch} onReset={handleReset}>
        <FilterField>
          <TextInput
            placeholder="제목/내용 검색"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
        </FilterField>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localTarget}
            onChange={e => setLocalTarget(e.target.value as TargetFilter)}
          >
            <option value="ALL">전체 대상앱</option>
            <option value="BUSINESS">비즈니스앱</option>
            <option value="GUARDIAN">보호자앱</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localCategory}
            onChange={e => setLocalCategory(e.target.value as CategoryFilter)}
          >
            <option value="ALL">전체 카테고리</option>
            <option value="SYSTEM">시스템</option>
            <option value="MAINTENANCE">점검</option>
            <option value="EVENT">이벤트</option>
            <option value="GENERAL">일반</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-28">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataGrid<Announcement>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.id)}
        emptyMessage="등록된 공지사항이 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
